import { useEffect, useState } from "react";
import { Star, Fire, Lightning, Medal, Trophy, CalendarCheck, ClipboardText, CheckCircle, Crown, Lock } from "@phosphor-icons/react";
import api from "../lib/api";

const ICONS = { Star, Fire, Lightning, Medal, Trophy, CalendarCheck, ClipboardText, CheckCircle, Crown };

export default function BadgeWall({ clientId, compact = false, refreshKey = 0 }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(`/clients/${clientId}/badges`);
        if (alive) setData(d);
      } catch (_e) {
        if (alive) setData(null);
      }
    })();
    return () => { alive = false; };
  }, [clientId, refreshKey]);

  if (!data) return null;

  if (compact) {
    const earned = data.badges.filter((b) => b.earned);
    if (earned.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2" data-testid="badge-chips">
        {earned.map((b) => {
          const Icon = ICONS[b.icon] || Star;
          return (
            <span key={b.code} className="inline-flex items-center gap-1.5 px-2.5 py-1 border text-xs"
              style={{ borderColor: `${b.color}66`, color: b.color, backgroundColor: `${b.color}14` }}
              title={b.description} data-testid={`badge-chip-${b.code}`}>
              <Icon size={13} weight="fill" /> {b.name}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <section data-testid="badge-wall">
      <div className="tracker text-zinc-500 mb-3">
        [ ACHIEVEMENTS · {data.earned_count}/{data.total} UNLOCKED ]
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {data.badges.map((b) => {
          const Icon = ICONS[b.icon] || Star;
          const pct = Math.min(100, Math.round((b.progress.current / b.progress.target) * 100));
          return b.earned ? (
            <div key={b.code} className="card-flat p-4 text-center relative overflow-hidden"
              style={{ borderColor: `${b.color}55` }} data-testid={`badge-${b.code}`}>
              <div className="absolute inset-0 opacity-10" style={{ backgroundColor: b.color }} />
              <Icon size={28} weight="fill" className="mx-auto mb-2" style={{ color: b.color }} />
              <div className="font-semibold text-sm">{b.name}</div>
              <div className="text-[10px] text-zinc-500 mt-1">{b.description}</div>
              <div className="tracker text-[9px] mt-2" style={{ color: b.color }}>
                {new Date(b.earned_at).toLocaleDateString()}
              </div>
            </div>
          ) : (
            <div key={b.code} className="card-flat p-4 text-center opacity-70" data-testid={`badge-${b.code}`}>
              <div className="relative inline-block mb-2">
                <Icon size={28} weight="duotone" className="text-zinc-600" />
                <Lock size={12} weight="fill" className="absolute -bottom-1 -right-1 text-zinc-500" />
              </div>
              <div className="font-semibold text-sm text-zinc-400">{b.name}</div>
              <div className="text-[10px] text-zinc-600 mt-1">{b.description}</div>
              <div className="mt-2">
                <div className="w-full h-1 bg-white/10 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-brand" style={{ width: `${pct}%` }} />
                </div>
                <div className="tracker text-[9px] text-zinc-600 mt-1" data-testid={`badge-progress-${b.code}`}>
                  {b.progress.current}/{b.progress.target}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
